/**
 * 算法版本号解析统一工具（前端）。
 *
 * **后端镜像：`simulation_platform/utils/version_tag.py` 的
 * `extract_prediction_data_version`** —— 任一改动必须同步另一侧并保持逐字等价。
 *
 * 把散落在 CreatePredictionUpdateJobModal / CreateJobModal 的版本号解析收敛到此处，
 * 消除手抄拷贝。
 *
 * 版本号形态示例：
 *   - `x86.20260826.release.10.11.24`  数字基线
 *   - `x86.20260930.release.AD3.0.5`   AD 字母基线（新格式）
 *   - `20260911.daily.sim.orin_dev.retry2`  daily 命名
 *   - `20261026.simulation.test.1.02`  平台构建流水号（不是预测数据版本）
 */

// 平台构建产物流水号：`{YYYYMMDD}.simulation.{env}.{N}.{NN}`（`20261026.simulation.test.1.02`）。
// 平台自动生成的仿真版本号，真正的预测数据版本（AD3.1.3 / release.10.12.34）不在串里，
// 误当 dataVersion 会造出 `prediction.20261026.simulation.test.1.02` 脏版本 → 一律不收。
const SIMULATION_SERIAL_RE = /\d{8}\.simulation\.[A-Za-z0-9_]+\.\d+\.\d+/i;

/**
 * 从算法版本号 / 包地址抽「预测数据版本号」（dataVersion，如 `AD3.1.3` / `release.10.12.34`）。
 *
 * **后端镜像：`simulation_platform/utils/version_tag.py` 的
 * `extract_prediction_data_version` —— 逐字等价，改一侧必须同步另一侧。**
 * 解析顺序（严格保序）：
 *   1. 去首尾空白与尾部 `/`；尾部形如 `(...)` 的注记（如 `(2026-10-28)`）只是备注，
 *      剥掉后按剩余串继续解析；
 *   2. 含 `release.` → 取其后到串尾（遇 `/` / 空白截断）：后段首字符为数字 →
 *      保留 `release.` 前缀（`release.10.12.34`），否则去前缀（`AD3.1.3`）；
 *   3. 否则含 `.daily.sim.` / `.daily.` → 取其后（同样截断），去尾部 `.retryN`；
 *   4. 否则是 BOS 路径（含 `/`）→ 取末段按上述口径重解析；
 *   5. 否则无空白 → 原样；含空白 → null。
 * 每个分支抽出的值都会再剥一次包产物包装（`x86_64_` 前缀、`-OrinX.Y.Z` 后缀、
 * `.tgz` / `.tar` 扩展名），与后端 `_strip_package_artifact` 等价。
 *
 * 样例：`x86.20261028.release.AD3.1.3` → `AD3.1.3`；
 * `20261010.daily.sim.release.10.12.34` → `release.10.12.34`；
 * `20261010.daily.sim.orin_dev` → `orin_dev`；`bos://bucket/a/b/AD3.1.3` → `AD3.1.3`。
 * 平台构建流水号（`20261026.simulation.test.1.02` /
 * `bos://.../x86_64_20261026.simulation.test.1.02-Orin6.0.9.0.tgz`）不是预测数据版本
 * → 返回 null，由调用方显式填 dataVersion。
 */
export function extractPredictionDataVersion(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  let s = String(raw).trim();
  if (!s) return null;
  // 1. 去尾部 `/`（可能多个）；尾部 (...) 只是注记，剥掉后按剩余串解析
  s = s.replace(/\/+$/, '').trim();
  if (!s) return null;
  for (;;) {
    const stripped = s.replace(/\([^()]*\)\s*$/, '').replace(/\/+$/, '').trim();
    if (stripped === s) break;
    s = stripped;
  }
  if (!s) return null;

  // 与后端 `_cut_tail_at_separator` 同义：取第一个 `/` 或空白之前的部分
  const cutTail = (value: string) => value.split(/[/\s]/, 1)[0].trim();
  // 与后端 `_strip_package_artifact` 同义：剥包产物包装（归档扩展名 / -Orin 后缀 / x86_64_ 前缀）
  const stripArtifact = (value: string) => value
    .replace(/\.(?:tar\.gz|tgz|tar)$/i, '')
    .replace(/-Orin[\d.]+$/i, '')
    .replace(/^x86_64_/i, '');

  const lower = s.toLowerCase();
  // 2. release 口径
  const relIdx = lower.indexOf('release.');
  if (relIdx >= 0) {
    const tail = stripArtifact(cutTail(s.slice(relIdx + 'release.'.length)));
    if (!tail) return null;
    return /^[0-9]/.test(tail) ? `release.${tail}` : tail;
  }

  // 3. daily 命名口径（`.daily.sim.` 优先；`.daily.` 是它的子串）
  for (const marker of ['.daily.sim.', '.daily.']) {
    const idx = lower.indexOf(marker);
    if (idx >= 0) {
      const tail = stripArtifact(cutTail(s.slice(idx + marker.length)).replace(/\.retry\d+$/i, ''));
      if (!tail || SIMULATION_SERIAL_RE.test(tail)) return null;
      return tail;
    }
  }

  // 4. BOS 路径：取末段重解析（末段可能是纯版本号，如 .../AD3.1.3）
  if (s.includes('/')) {
    const last = s.slice(s.lastIndexOf('/') + 1).trim();
    if (last && last !== s) return extractPredictionDataVersion(last);
    return null;
  }

  // 5. 纯值：无空白才返回（末段是包文件名时剥掉包装）
  if (/\s/.test(s)) return null;
  const val = stripArtifact(s);
  // 平台构建流水号（20261026.simulation.test.1.02）不是预测数据版本
  if (!val || SIMULATION_SERIAL_RE.test(val)) return null;
  return val;
}

/**
 * 从算法版本号按动态 `presetKeys` 列表匹配出命中的 bazel 预设 key。
 *
 * 版本示例：`x86.20260623.release.10.10.20` / `20260430.release.10.9.5`。
 * - 前缀 `x86.` 可有可无；日期段可有可无；`release` 必须出现；
 * - 命中 `presetKeys` 中任一 key（按 `release.<key>` 边界匹配）即返回该 key。
 * 命中返回 key 字符串，未命中或 presetKeys 为空返回 null。
 */
export function detectAlgoVersionPreset(raw: string, presetKeys: string[]): string | null {
  if (!raw || presetKeys.length === 0) return null;
  const v = raw.trim();
  if (!v) return null;
  // 不处理 obs:// / bos:// 完整路径，那是显式包路径
  if (/^(obs|bos):\/\//i.test(v)) return null;
  const escaped = presetKeys
    .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .filter((k) => k.length > 0);
  if (escaped.length === 0) return null;
  const re = new RegExp(`release[.\\-_]?(${escaped.join('|')})(?:[.\\-_]|$)`, 'i');
  const m = v.match(re);
  if (!m) return null;
  // 返回原始 key（未转义），用于查 bazelPresets
  return presetKeys.find((k) => k.toLowerCase() === m[1].toLowerCase()) ?? null;
}

/** 「预测数据版本」展示文案：``default`` 统一显示为「record 包内原始数据」，其余原样。 */
export function formatPredictionDataVersion(value: string | null | undefined): string {
  const text = (value ?? '').trim();
  return text.toLowerCase() === 'default' ? 'record 包内原始数据' : text;
}
